/**
 * Tree integrity validation
 * Functions for checking structural integrity, references, and circular dependencies
 */

import { TodoTree } from '../../types/todo';

/**
 * Validates TodoTree integrity by checking references and structure
 */
export function validateTodoTreeIntegrity(tree: TodoTree): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const allIds = new Set(Object.keys(tree));

  // Check for root node
  const rootNodes = Object.values(tree).filter(item => item.name === 'Everything');
  if (rootNodes.length !== 1) {
    errors.push('Tree must have exactly one root node named "Everything"');
  }

  // Check references
  for (const [, item] of Object.entries(tree)) {
    for (const childId of item.children) {
      if (!allIds.has(childId)) {
        errors.push(`Item "${item.name}" references non-existent child: ${childId}`);
      }
    }
  }

  // Check for circular references
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function hasCircularReference(nodeId: string): boolean {
    if (recursionStack.has(nodeId)) {
      return true; // Found a back edge
    }
    if (visited.has(nodeId)) {
      return false; // Already processed
    }

    visited.add(nodeId);
    recursionStack.add(nodeId);

    const node = tree[nodeId];
    if (node) {
      for (const childId of node.children) {
        if (hasCircularReference(childId)) {
          return true;
        }
      }
    }

    recursionStack.delete(nodeId);
    return false;
  }

  for (const nodeId of Array.from(allIds)) {
    if (hasCircularReference(nodeId)) {
      errors.push(`Circular reference detected in tree structure involving node: ${nodeId}`);
      break; // One circular reference error is enough
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Find orphaned nodes (nodes not referenced by any parent except root)
 */
export function findOrphanedNodes(tree: TodoTree, rootId: string): string[] {
  const referencedIds = new Set<string>();
  Object.values(tree).forEach(item => {
    item.children.forEach(childId => referencedIds.add(childId));
  });

  return Object.keys(tree).filter(id =>
    id !== rootId && !referencedIds.has(id)
  );
}
